/**
 * TypeScript view of the shared JSON formats (source of truth: shared/schema/*.json).
 * Keep in sync with the schemas; the vitest in ./schema.test.ts validates fixtures against them.
 */

export type CellId = string;
/** `[row, col]` (0-indexed) or an explicit cell id for `graph` topologies. */
export type CellRef = [number, number] | string;
export type Localized = { fr?: string; en?: string } & Record<string, string | undefined>;

export interface DigitRange {
  min: number;
  max: number;
}

export interface RegionSpec {
  id?: string;
  cells: CellRef[];
}

export interface RectTopology {
  kind: "rect";
  rows: number;
  cols: number;
  digits?: DigitRange;
  regions?: RegionSpec[];
}

export interface GraphTopology {
  kind: "graph";
  digits?: DigitRange;
  cells: { id: string; row?: number; col?: number }[];
  adjacency?: [string, string][];
  diagonalAdjacency?: [string, string][];
  rows?: string[][];
  cols?: string[][];
  regions?: RegionSpec[];
}

export type Topology = RectTopology | GraphTopology;

export interface Given {
  r: number;
  c: number;
  v: number;
}

export interface VariableSpec {
  name: string;
  domain?: string | number[];
}

/** A constraint instance. Params depend on the type (see shared/constraints/builtin.json). */
export interface ConstraintInstance {
  type: string;
  cells?: CellRef[];
  cells2?: CellRef[];
  cell?: CellRef;
  at?: CellRef;
  bulb?: CellRef[];
  body?: CellRef[];
  towers?: CellRef[];
  /** yin_yang clues: black circles (shaded) and white circles (unshaded). */
  shaded?: CellRef[];
  unshaded?: CellRef[];
  side?: "top" | "bottom" | "left" | "right";
  index?: number;
  direction?: string;
  scope?: string;
  sum?: number;
  sumExpr?: string;
  value?: number | string;
  valueExpr?: string;
  relation?: "sum" | "product" | "difference" | "ratio";
  negative?: boolean;
  distinct?: boolean;
  label?: string;
  values?: number[];
  markers?: number[];
  expr?: string;
  cmp?: string;
  vars?: string[];
  fill?: string;
  text?: string;
  [key: string]: unknown;
}

export interface Cosmetic {
  type: "text" | "circle" | "rect" | "line" | "arrow" | "diamond";
  at?: CellRef;
  cells?: CellRef[];
  /**
   * Line/arrow path in continuous grid coordinates, `[row, col]`: an integer is a grid corner,
   * a `.5` the middle of a cell. Takes precedence over `cells`, which only names the cells the
   * path crosses and so cannot express a path drawn along the edges.
   */
  points?: [number, number][];
  value?: string;
  color?: string;
  fill?: string;
  size?: number;
  thickness?: number;
  position?: "center" | "edge" | "corner" | "outside";
  offset?: [number, number];
  [key: string]: unknown;
}

export interface SolutionShape {
  digits: { required: boolean };
  regions?: { required: boolean };
  modifiers?: { required: boolean; values?: string[] };
  coloring?: { required: boolean; values?: string[] };
  [layer: string]: { required: boolean; values?: string[] } | undefined;
}

export type CheckStrategy = "unique" | "constraints" | "either";

export interface GridSudokuDefinition {
  puzzleType: "grid_sudoku";
  typeVersion: number;
  topology: Topology;
  solutionShape: SolutionShape;
  givens?: Given[];
  variables?: VariableSpec[];
  globalConstraints: ConstraintInstance[];
  constraints: ConstraintInstance[];
  cosmetics?: Cosmetic[];
  rulesText?: Localized;
  checkStrategy?: CheckStrategy;
  /** Constraints an importer could not translate. Never silently dropped. */
  unsupported?: Record<string, unknown>[];
  meta?: Record<string, unknown>;
}

/** Solution / submission: a dictionary of layers keyed by cell id (never a matrix). */
export interface DigitLayers {
  digits: Record<CellId, number>;
  regions?: Record<CellId, string>;
  modifiers?: Record<CellId, string>;
  coloring?: Record<CellId, string>;
  /** Second digits of Schrödinger cells, as strings like every non-digit layer. */
  schrodinger?: Record<CellId, string>;
}

export interface GridSudokuSubmission {
  layers: DigitLayers;
}

/** A yin-yang style shading decision; the `coloring` layer's vocabulary. */
export type Shade = "shaded" | "unshaded";

/** Autosaved progress. Opaque to the core; the type owns its shape and its migrations. */
export interface GridSudokuProgress {
  version: 1;
  digits: Record<CellId, number>;
  centerMarks: Record<CellId, number[]>;
  cornerMarks: Record<CellId, number[]>;
  colors: Record<CellId, number[]>;
  /** Solution shading (yin-yang); absent in blobs saved before it existed. */
  shading?: Record<CellId, Shade>;
  /** The regions the solver draws (chaos construction): a label per cell, any string. */
  regions?: Record<CellId, string>;
  /** Cell modifiers (doublers): `double` on the cells marked, nothing elsewhere. */
  modifiers?: Record<CellId, string>;
  /** The second digit of each Schrödinger cell: present on those cells only. */
  superposition?: Record<CellId, number>;
}

/** Level-1 catalogue entry (shared/constraints/builtin.json + tenant definitions). */
export interface ConstraintDefinition {
  selector: { kind: string; [key: string]: unknown };
  predicates: Record<string, unknown>[];
  params?: Record<string, { type?: string; default?: unknown; enum?: unknown[] }>;
  global?: boolean;
  supportsNegative?: boolean;
  negativeFamily?: string;
  render: { shape: string; [key: string]: unknown };
  labels?: Record<string, string>;
}

export interface Catalogue {
  version: number;
  constraints: Record<string, ConstraintDefinition>;
  aliases: Record<string, { type: string; defaults?: Record<string, unknown>; transform?: string }>;
  negativeFamilies?: Record<string, { selector: string }>;
}

export function rectCellId(row: number, col: number): CellId {
  return `r${row}c${col}`;
}

export function parseRectCellId(id: CellId): [number, number] | null {
  const m = /^r(\d+)c(\d+)$/.exec(id);
  return m ? [Number(m[1]), Number(m[2])] : null;
}
